import { apiGet, apiPost, apiPut, apiDelete } from './apiUtils';

const adminCrmService = {
    // Assign a subscription plan to a CRM user (matching mobile adminCrmService.js)
    assignPlan: async (userId, planId, days) => {
        try {
            const response = await apiPost(`/api/admin/crm-users/${userId}/assign-plan`, { plan_id: planId, days });
            return { success: true, data: response.data || response };
        } catch (error) {
            console.error('Error assigning plan:', error);
            return { success: false, message: error.response?.data?.message || error.message };
        }
    },

    // Extend subscription for a CRM user (matching mobile adminCrmService.js)
    extendSubscription: async (userId, days) => {
        try {
            const response = await apiPost(`/api/admin/crm-users/${userId}/extend-subscription`, { days });
            return { success: true, data: response.data || response };
        } catch (error) {
            console.error('Error extending subscription:', error);
            return { success: false, message: error.response?.data?.message || error.message };
        }
    },

    // Billing Summary
    getBillingSummary: async (period = 'this_month') => {
        try {
            const response = await apiGet('/api/admin/billing-summary', { period });
            return {
                success: true,
                data: response.data || response
            };
        } catch (error) {
            console.error('Error fetching billing summary:', error);
            return {
                success: false,
                error: error.message,
                data: null
            };
        }
    },

    // Get Invoices
    getInvoices: async (params = {}) => {
        try {
            const response = await apiGet('/api/admin/billing-summary/invoices', params);
            return {
                success: true,
                data: response.data || response
            };
        } catch (error) {
            console.error('Error fetching invoices:', error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    },

    // Get Expenses
    getExpenses: async (params = {}) => {
        try {
            const response = await apiGet('/api/admin/expenses', params);
            return {
                success: true,
                data: response.data || response
            };
        } catch (error) {
            console.error('Error fetching expenses:', error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    },

    // Upload Expense File
    uploadExpenseFile: async (formData) => {
        try {
            const response = await apiPost('/api/admin/expenses/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return {
                success: true,
                data: response.data || response
            };
        } catch (error) {
            console.error('Error uploading expense file:', error);
            return {
                success: false,
                error: error.message,
                data: null
            };
        }
    },

    // Create Expense
    createExpense: async (data) => {
        try {
            const response = await apiPost('/api/admin/expenses', data);
            return {
                success: true,
                data: response.data || response
            };
        } catch (error) {
            console.error('Error creating expense:', error);
            return {
                success: false,
                error: error.message,
                data: null
            };
        }
    },

    // Download Invoice PDF
    downloadInvoicePDF: async (invoiceId) => {
        try {
            const response = await apiGet(`/api/admin/invoices/${invoiceId}/pdf`, {}, {
                responseType: 'blob'
            });
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('Error downloading invoice PDF:', error);
            return {
                success: false,
                error: error.message,
                data: null
            };
        }
    },

    getFullProfile: async (userId) => {
        try {
            const response = await apiGet(`/api/admin/crm-users/${userId}/full-profile`);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    updateProfile: async (userId, data) => {
        try {
            const response = await apiPut(`/api/admin/crm-users/${userId}/profile`, data);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    getUserProjects: async (userId, params = {}) => {
        try {
            const query = new URLSearchParams(params).toString();
            const response = await apiGet(`/api/admin/crm-users/${userId}/projects${query ? '?' + query : ''}`);
            return { success: true, items: response.data?.items || response.items || [], total: response.data?.total || response.total || 0 };
        } catch (error) {
            return { success: false, error: error.message, items: [], total: 0 };
        }
    },

    createProjectForBuilder: async (userId, data) => {
        try {
            const response = await apiPost(`/api/admin/crm-users/${userId}/projects`, data);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, message: error.response?.data?.message || error.message };
        }
    },

    updateProjectForBuilder: async (userId, projectId, data) => {
        try {
            const response = await apiPut(`/api/admin/crm-users/${userId}/projects/${projectId}`, data);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, message: error.response?.data?.message || error.message };
        }
    },

    deleteProjectForBuilder: async (userId, projectId) => {
        try {
            const response = await apiDelete(`/api/admin/crm-users/${userId}/projects/${projectId}`);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, message: error.response?.data?.message || error.message };
        }
    },

    approveProjectFromCrm: async (userId, projectId) => {
        try {
            const response = await apiPost(`/api/admin/crm-users/${userId}/projects/${projectId}/approve`, {});
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, message: error.response?.data?.message || error.message };
        }
    },

    sendProjectToTask: async (userId, projectId) => {
        try {
            const response = await apiPost(`/api/admin/crm-users/${userId}/projects/${projectId}/send-to-task`, {});
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, message: error.response?.data?.message || error.message };
        }
    },

    // Mandate projects (builder exclusive rights) — { builder_name, start_date, end_date, file_path }
    getExclusiveRights: async (userId) => {
        try {
            return await apiGet('/api/admin/exclusive-rights', { user_id: userId });
        } catch (error) {
            return { success: false, message: error.message, data: [] };
        }
    },

    createExclusiveRight: async (userId, data) => {
        try {
            return await apiPost('/api/admin/exclusive-rights', { ...data, user_id: String(userId) });
        } catch (error) {
            return { success: false, message: error.message };
        }
    },

    updateExclusiveRight: async (rightId, data) => {
        try {
            return await apiPut(`/api/admin/exclusive-rights/${rightId}`, data);
        } catch (error) {
            return { success: false, message: error.message };
        }
    },

    deleteExclusiveRight: async (rightId) => {
        try {
            return await apiDelete(`/api/admin/exclusive-rights/${rightId}`);
        } catch (error) {
            return { success: false, message: error.message };
        }
    },

    getUserReviews: async (userId) => {
        try {
            const response = await apiGet(`/api/admin/crm-users/${userId}/reviews`);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    getUserBillingSummary: async (userId) => {
        try {
            const response = await apiGet(`/api/admin/crm-users/${userId}/billing-summary`);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    getUserMembership: async (userId) => {
        try {
            const response = await apiGet(`/api/admin/crm-users/${userId}/membership`);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    getUserStatus: async (userId) => {
        try {
            const response = await apiGet(`/api/admin/crm-users/${userId}/status`);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    disableUser: async (userId, reason) => {
        try {
            const response = await apiPost(`/api/admin/crm-users/${userId}/disable`, { reason });
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    enableUser: async (userId) => {
        try {
            const response = await apiPost(`/api/admin/crm-users/${userId}/enable`);
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    deleteUserPermanently: async (userId, reason) => {
        try {
            const response = await apiDelete(`/api/admin/crm-users/${userId}/delete`, { body: { reason } });
            return { success: true, data: response.data || response };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    },

    verifyPassword: async (password) => {
        const response = await apiPost('admin/crm-access/verify-password', { password });
        return response;
    },

    sendOtp: async (userId) => {
        const response = await apiPost('admin/crm-access/send-otp', { userId });
        return response;
    },

    startSession: async (userId, otp) => {
        const response = await apiPost('admin/crm-access/start-session', { userId, otp });
        return response;
    },

    endSession: async (targetUserId) => {
        const response = await apiPost('admin/crm-access/end-session', { targetUserId });
        return response;
    },
};

export default adminCrmService;
